import { Button } from '@/components/ui/button';
import {
  useBackfillSingleCandidateFromPf,
  useCandidate,
  useCandidateNotes,
  useDeleteCandidateNote,
} from '@/hooks/use-candidates';
import type { CandidateNote } from '@/types/candidates';
import { Cloud, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CandidateNoteComposer } from './candidate-note-composer';
import { CandidateNoteEditDialog } from './candidate-note-edit-dialog';
import { CandidateNoteItem } from './candidate-note-item';
import { isConsentLocked } from '../lib/consent-actions';

interface CandidateNotesFeedProps {
  candidateId: string;
  currentUserId: string | null;
}

export function CandidateNotesFeed({ candidateId, currentUserId }: CandidateNotesFeedProps) {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateNotes(candidateId);
  const deleteNote = useDeleteCandidateNote(candidateId);
  const pullFromPf = useBackfillSingleCandidateFromPf(candidateId);
  // HW-1055: a candidate who revoked consent is read-only until anonymised —
  // no new note, no edit or delete, no PeopleForce pull.
  const locked = isConsentLocked(useCandidate(candidateId).data);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [editingNote, setEditingNote] = useState<CandidateNote | null>(null);
  const [pullResult, setPullResult] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const notes = data?.pages.flatMap((page) => page.data) ?? [];

  const triggerPull = () => {
    setPullResult(null);
    pullFromPf.mutate(undefined, {
      onSuccess: (res) => {
        const d = res.data;
        const created = d.notesCreated + d.cvsCreated;
        if (created === 0 && d.notesUpdated === 0 && d.cvsSkipped === 0 && d.notesSkippedEditedLocally === 0) {
          setPullResult({ ok: true, text: 'No new data in PeopleForce for this candidate.' });
        } else {
          setPullResult({ ok: true, text: res.message });
        }
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : 'Pull failed';
        setPullResult({ ok: false, text: msg });
      },
    });
  };

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-3 rounded-md border border-dashed border-hygge/30 bg-hygge/5 px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-foreground">Import from PeopleForce</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Pulls every PF note + the resume PDF for this one candidate. Idempotent — re-runs
            never overwrite notes you've edited locally.
          </p>
          {pullResult && (
            <p
              className={
                'mt-1 text-[11px] ' +
                (pullResult.ok ? 'text-green-700 dark:text-green-400' : 'text-destructive')
              }
            >
              {pullResult.text}
            </p>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 gap-1.5 border-hygge/40 text-hygge hover:bg-hygge/10 hover:text-hygge"
          disabled={pullFromPf.isPending || locked}
          onClick={triggerPull}
        >
          {pullFromPf.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Cloud className="h-3.5 w-3.5" />
          )}
          {pullFromPf.isPending ? 'Pulling…' : 'Pull from PeopleForce'}
        </Button>
      </div>

      {locked ? (
        <p className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          Consent revoked — notes can no longer be added, edited or deleted.
        </p>
      ) : (
        <CandidateNoteComposer candidateId={candidateId} />
      )}

      {isLoading ? (
        <div className="rounded-lg border border-border p-6 text-sm text-muted-foreground">
          Loading…
        </div>
      ) : error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load notes.
        </div>
      ) : notes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-10 text-center">
          <p className="text-sm text-muted-foreground">
            No notes yet. Be the first to add one.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-background">
          <ul className="divide-y divide-border px-5">
            {notes.map((note) => (
              <CandidateNoteItem
                key={note.id}
                note={note}
                currentUserId={currentUserId}
                onEdit={locked ? undefined : () => setEditingNote(note)}
                onDelete={
                  locked
                    ? undefined
                    : () => {
                        if (window.confirm('Delete this note?')) {
                          deleteNote.mutate(note.id);
                        }
                      }
                }
                isDeleting={deleteNote.isPending && deleteNote.variables === note.id}
              />
            ))}
          </ul>
          <div ref={sentinelRef} className="px-5 py-3 text-center">
            {isFetchingNextPage ? (
              <span className="text-xs text-muted-foreground">Loading more…</span>
            ) : hasNextPage ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchNextPage()}
                className="text-xs"
              >
                Load more
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">End of notes</span>
            )}
          </div>
        </div>
      )}

      <CandidateNoteEditDialog
        candidateId={candidateId}
        note={editingNote}
        open={editingNote !== null}
        onOpenChange={(open) => {
          if (!open) setEditingNote(null);
        }}
      />
    </section>
  );
}
